
import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Save } from 'lucide-react';
import { toast } from 'sonner';
import { useProducts } from '@/hooks/useProducts';
import { categoriesData } from '@/data/categoriesData';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';

import { productFormSchema, ProductFormValues } from './ProductFormSchema';
import ProductImageUpload from './ProductImageUpload';
import PromotionSection from './PromotionSection';
import ProductVariantsSection from './ProductVariantsSection';
import ProductDetailsFields from './ProductDetailsFields';

interface ColorVariant {
  id: string;
  name: string;
  hex: string;
}

interface SizeVariant {
  id: string;
  name: string;
}

interface ProductFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId?: string; // Optional product ID for editing
}

const ProductForm: React.FC<ProductFormProps> = ({ open, onOpenChange, productId }) => {
  const { addProduct, updateProduct, fetchProduct } = useProducts();
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [images, setImages] = useState<string[]>([]);
  const [colors, setColors] = useState<ColorVariant[]>([]);
  const [sizes, setSizes] = useState<SizeVariant[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [availableSubcategories, setAvailableSubcategories] = useState<{name: string, link: string}[]>([]);
  const [showColorOptions, setShowColorOptions] = useState(false);
  const [showSizeOptions, setShowSizeOptions] = useState(false);
  const [applyDiscount, setApplyDiscount] = useState(false);
  const [applyCoupon, setApplyCoupon] = useState(false);

  const form = useForm<ProductFormValues>({
    resolver: zodResolver(productFormSchema),
    defaultValues: {
      name: '',
      description: '',
      category: '',
      subcategory: '',
      buyPrice: '',
      sellPrice: '',
      discount: '',
      couponCode: '',
    },
  });

  useEffect(() => {
    const loadProduct = async () => {
      if (productId) {
        setIsEditMode(true);
        setLoading(true);
        try {
          const product = await fetchProduct(productId);
          if (product) {
            form.setValue('name', product.name);
            form.setValue('description', product.description);
            form.setValue('category', product.category);
            form.setValue('subcategory', product.subcategory || '');
            form.setValue('buyPrice', product.buyPrice?.toString() || '');
            form.setValue('sellPrice', product.sellPrice?.toString() || '');
            
            if (product.discount) {
              setApplyDiscount(true);
              form.setValue('discount', product.discount.toString());
            }
            
            if (product.couponCode) {
              setApplyCoupon(true);
              form.setValue('couponCode', product.couponCode);
            }
            
            setSelectedCategory(product.category);
            
            setImages(product.images || [product.imageUrl].filter(Boolean));
            
            if (product.colors && product.colors.length > 0) {
              setShowColorOptions(true);
              setColors(product.colors);
            }
            
            if (product.sizes && product.sizes.length > 0) {
              setShowSizeOptions(true);
              setSizes(product.sizes.map(size => ({
                id: crypto.randomUUID(),
                name: size
              })));
            }
          }
        } catch (error) {
          console.error('Error loading product:', error);
          toast.error('Failed to load product details');
        } finally {
          setLoading(false);
        }
      } else {
        setIsEditMode(false);
        form.reset();
        setImages([]);
        setColors([]);
        setSizes([]);
        setSelectedCategory('');
        setShowColorOptions(false);
        setShowSizeOptions(false);
        setApplyDiscount(false);
        setApplyCoupon(false);
      }
    };
    
    loadProduct();
  }, [productId, fetchProduct, form]);

  useEffect(() => {
    const categoryData = categoriesData.find(cat => cat.name === selectedCategory);
    if (categoryData) {
      setAvailableSubcategories(categoryData.subcategories);
      if (!isEditMode) {
        form.setValue('subcategory', '');
      }
    } else {
      setAvailableSubcategories([]);
    }
  }, [selectedCategory, form, isEditMode]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    if (images.length + files.length > 5) {
      toast.error('Maximum 5 images allowed');
      return;
    }

    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          setImages(prev => [...prev, e.target!.result as string]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (values: ProductFormValues) => {
    try {
      setLoading(true);
      
      if (images.length === 0) {
        toast.error('At least one product image is required');
        setLoading(false);
        return;
      }
      
      const productData = {
        name: values.name,
        description: values.description,
        category: values.category,
        subcategory: values.subcategory,
        price: Number(values.sellPrice),
        imageUrl: images[0],
        buyPrice: Number(values.buyPrice),
        sellPrice: Number(values.sellPrice),
        images: images,
        colors: showColorOptions ? colors : [],
        sizes: showSizeOptions ? sizes.map(s => s.name) : [],
        discount: applyDiscount ? Number(values.discount) : undefined,
        couponCode: applyCoupon ? values.couponCode : '',
      };
      
      if (isEditMode && productId) {
        await updateProduct(productId, productData);
        toast.success('Product updated successfully');
      } else {
        await addProduct(productData);
        toast.success('Product added successfully');
      }
      
      onOpenChange(false);
      
      form.reset();
      setImages([]);
      setColors([]);
      setSizes([]);
      setSelectedCategory('');
      setShowColorOptions(false);
      setShowSizeOptions(false);
      setApplyDiscount(false);
      setApplyCoupon(false);
    } catch (error) {
      console.error(isEditMode ? 'Error updating product:' : 'Error adding product:', error);
      toast.error(isEditMode ? 'Failed to update product' : 'Failed to add product');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Product' : 'Add New Product'}</DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <ProductImageUpload 
              images={images}
              onImageUpload={handleImageUpload}
              removeImage={removeImage}
              fileInputRef={fileInputRef}
            />
            
            <ProductDetailsFields 
              form={form}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              availableSubcategories={availableSubcategories}
            />
            
            <PromotionSection 
              form={form}
              applyDiscount={applyDiscount}
              setApplyDiscount={setApplyDiscount}
              applyCoupon={applyCoupon}
              setApplyCoupon={setApplyCoupon}
            />
            
            <ProductVariantsSection 
              showColorOptions={showColorOptions}
              setShowColorOptions={setShowColorOptions}
              colors={colors}
              setColors={setColors}
              showSizeOptions={showSizeOptions}
              setShowSizeOptions={setShowSizeOptions}
              sizes={sizes}
              setSizes={setSizes}
            />
            
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? (isEditMode ? 'Updating...' : 'Adding...') : isEditMode ? 'Update Product' : 'Add Product'}
                <Save className="ml-1 h-4 w-4" />
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default ProductForm;
